<template>
    <div>
        <div
            v-if="!emptyRows && !chartLoading"
            class="chart-container"
        >
            <v-chart
                :option="barChartOptions"
                :style="{ height: `${windowHeight * 0.39}px` }"
                class="chart"
                autoresize
            />
        </div>
        <div
            v-else-if="!chartLoading"
            :style="{ height: `${windowHeight * 0.39}px` }"
            class="flex flex-center"
        >
            <WText>
                {{ $t('visualization.customPlotPanels.attributesInfo.noData') }}
            </WText>
        </div>
    </div>
</template>
<script>
import moment from 'moment'
import { sortBy } from 'lodash'
import { formatNumber } from '@/utils/formatNumber'
import echartsColors from '@/charts/echartsColors'
import { SortDirection } from '../utils'

export default {
    name: "ScatterChart",
    props: {
        generalFiltering: {
            type: Object,
            required: true,
        },
        chartData: {
            type: Object,
            required: true,
        },
        chartCreationProps: {
            type: Object,
            required: true,
        },
        chartLoading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['get-chart-data', 'chartLoading'],
    data() {
        return {
            emptyRows: false,
            sortDirection: SortDirection.DESC,
            ownFilters: [],
            windowHeight: window.innerHeight - 100,
            baseDimension: { type: 'FREQUENCY' },
            additionalDimensions: [{ type: 'DURATION', measurement: 'AVERAGE' }],
            dimensionsTypeOptions: [
                {
                    value: 'FREQUENCY',
                    label: this.$t('visualization.customPlotPanels.frequency'),
                },
                {
                    value: 'DURATION',
                    label: this.$t('visualization.customPlotPanels.duration'),
                },
                {
                    value: 'ATTRIBUTE',
                    label: 'attribute',
                },
            ],
            dimensionMeasurementsOptions: [
                {
                    value: "AVERAGE",
                    label: "AVG",
                },
                {
                    value: "MAXIMUM",
                    label: "MAX",
                },
                {
                    value: "MINIMUM",
                    label: "MIN",
                },
                {
                    value: "STANDARD_DEVIATION",
                    label: "STD DEV",
                },
                {
                    value: "MOST_FREQUENT",
                    label: "MOST",
                },
            ],
            barChartOptions: {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross',
                    },
                },
                grid: {
                    bottom: 40,
                    top: 40,
                    left: 40,
                },
                xAxis: {
                    splitLine: {
                        lineStyle: {
                            type: 'dashed',
                        },
                    },
                    nameLocation: 'middle',
                    nameTextStyle: {
                        align: 'left',
                        verticalAlign: 'top',
                        padding: [15, 0],
                    },
                    axisLabel: {
                        interval: 2,
                        hideOverlap: true,
                        formatter: (value) => {
                            const maxLength = 15
                            return value.length > maxLength ? `${value.slice(0, maxLength) }...` : value
                        },
                    },
                },
                yAxis: {
                    splitLine: {
                        lineStyle: {
                            type: 'dashed',
                        },
                    },
                    axisLabel: {
                        formatter: (value) => {
                            const maxLength = 15
                            return value.length > maxLength ? `${value.slice(0, maxLength) }...` : value
                        },
                    },
                },
                series: {
                    symbolSize: 20,
                    data: [],
                    type: 'scatter',
                    itemStyle: {
                        color: echartsColors.series.secondary,
                    },
                },
            },
        }
    },
    watch: {
        chartData: {
            handler(newVal) {
                this.emptyRows = (newVal.rows === null || newVal.rows.length === 0)
                if (!this.emptyRows) {
                    this.updateChart(newVal.rows)
                }
            },
            deep: true,
        },
    },
    mounted() {
    },
    methods: {
        formatDuration(duration) {
            return moment.duration(duration, 'seconds').format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false })
        },
        formatFrequency(value) {
            const options = {
                decimals: 0, decimalSeparator: '.', compress: true, trimRight: true,
            }
            return formatNumber(value, options)
        },
        resetChart() {
            this.barChartOptions.series.data = []
        },
        getData() {
            this.$emit('chartLoading', true)
            this.resetChart()
            const generalParams = {
                generalFilters: this.generalFiltering.generalFilters || [],
                dashboardFilters: this.generalFiltering.dashboardFilters || [],
                ownFilters: this.generalFiltering.ownFilters || [],
                filterSets: this.generalFiltering.filterSets || [],
                operator: this.generalFiltering.operator,
                sortDirection: this.sortDirection,
                additionalDimensions: this.additionalDimensions,
                baseDimension: this.baseDimension,
            }
            this.$emit("get-chart-data", generalParams)
        },
        updateChart(data) {
            const formattedData = data.map((d) => ({
                duration: d.dimensions[0].value,
                frequency: d.baseDimensionValue,
                variantId: d.variantId,
            }))
            const sortedData = sortBy(formattedData, 'duration')
            this.barChartOptions = {
                ...this.barChartOptions,
                series: {
                    ...this.barChartOptions.series,
                    data: sortedData.map(({ duration, frequency, variantId }) => ({
                        value: [
                            duration,
                            frequency,
                        ],
                        name: variantId,
                    })),
                },
                yAxis: {
                    ...this.barChartOptions.yAxis,
                    name: this.$t(`visualization.dashboard.axisLabels.frequency`),
                    axisLabel: {
                        formatter: (value) => this.formatFrequency(value),
                    },
                },
                xAxis: {
                    ...this.barChartOptions.xAxis,
                    name: this.$t(`visualization.dashboard.axisLabels.duration`),
                    axisLabel: {
                        formatter: (value) => this.formatDuration(value),
                    },
                },
                tooltip: {
                    ...this.barChartOptions.tooltip,
                    formatter: ([value]) => (value
                        ? `
                            ${this.$t('visualization.dashboard.axisLabels.variantId')}: ${value.name}
                            <br>
                            ${this.$t('visualization.dashboard.axisLabels.frequency')}: ${this.formatFrequency(value.value[1])}
                            <br>
                            ${this.$t('visualization.dashboard.axisLabels.duration')}: ${this.formatDuration(value.value[0])}
                            `
                        : ''),
                },
            }
            this.$emit('chartLoading', false)
        },
    },
}
</script>
<style lang="scss">
    .chart {
        width: 100%;
        min-width: 100px;
        margin: 0 !important;

        & + .chart {
            margin-left: 30px;

            @media screen and (max-width: $lg) {
                margin-left: 0px;
            }
        }
    }
</style>
